import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from '@mui/material';
import { useRouteLoaderData } from 'react-router-dom';
import TimelineDot from '@mui/lab/TimelineDot';
import {
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  Timeline,
} from '@mui/lab';
import LoadingButton from '@mui/lab/LoadingButton';
import { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent';
import useLiveData from '@/hooks/useLiveData';
import { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useEffect } from 'react';
import api from '@/services/ApiService';
import { toast } from 'react-toastify';

const formatDate = (date) => {
  const d = new Date(date);
  return {
    display: `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${d.getFullYear()} ${d
      .getHours()
      .toString()
      .padStart(2, '0')}:${d
      .getMinutes()
      .toString()
      .padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')} Uhr`,
    timestamp: d.getTime(),
  };
};

const Comments = forwardRef(({ entityId, entityName }, ref) => {
  const { users, me } = useRouteLoaderData('auth');
  const [open, setOpen] = useState(false);
  const [newComment, setNewComment] = useState({ title: '', description: '' });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [comments, setComments] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const {
          data: { result },
        } = await api.get(`/comment?entityId=${entityId}`);
        setComments(result);
      } catch (error) {
        toast.error(`Fehler beim Laden der Kommentare: ${error.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [entityId]);

  useLiveData('comment');

  useImperativeHandle(ref, () => ({
    openDialog: () => setOpen(true),
    closeDialog: () => setOpen(false),
  }));

  const filteredComments = useMemo(() => {
    if (!comments) return [];
    return comments
      .map((comment) => {
        const owner = users.find((user) => user?._id === comment.createdBy);
        return {
          date: formatDate(comment?.createdAt),
          user: owner
            ? `${owner.firstName} ${owner.lastName}`
            : 'Unbekannter Nutzer',
          title: comment.title,
          description: comment.description,
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [comments, users, entityId]);

  const mergedData = useMemo(() => {
    return filteredComments.sort((a, b) => {
      return b.date.timestamp - a.date.timestamp;
    });
  }, [filteredComments]);

  const handleClose = () => {
    if (!saving) {
      setOpen(false);
      setNewComment({ title: '', description: '' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const savedComment = {
        title: newComment.title || '',
        description: newComment.description || '',
        entityId,
        entityName,
        public: true,
      };

      const { data } = await api.post('/comment', savedComment);

      setNewComment({ title: '', description: '' });
      setComments((prev) => [
        ...prev,
        {
          _id: data.insertedId,
          ...savedComment,
          createdBy: me._id,
          createdAt: new Date(),
        },
      ]);
      setOpen(false);
    } catch (error) {
      toast.error(`Fehler beim Speichern des Kommentars: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', padding: 2 }}>
      <Box sx={{ overflowY: 'auto' }}>
        <Timeline
          position="right"
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: 0.3,
            },
            padding: 0,
          }}
        >
          {loading ? (
            Array.from(new Array(4)).map((_, index) => (
              <TimelineItem key={`skeleton-${index}`}>
                <TimelineOppositeContent>
                  <Skeleton
                    variant="text"
                    width={100}
                    height={15}
                    sx={{ ml: 'auto' }}
                  />
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot sx={{ bgcolor: 'action.disabled' }} />
                  {index < 3 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Skeleton variant="text" width={120} height={15} />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={25}
                    sx={{ my: 0.5 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={30}
                    sx={{ borderRadius: 1 }}
                  />
                </TimelineContent>
              </TimelineItem>
            ))
          ) : (
            <>
              {mergedData.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineOppositeContent color="textSecondary">
                    <Typography
                      variant="caption"
                      display="block"
                      color="textSecondary"
                    >
                      {item.date.display}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot />
                    {index < mergedData.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="caption" color="primary">
                      {item.user}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.description}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}

              {mergedData.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ pl: 4, color: 'text.secondary' }}
                >
                  Keine Kommentare vorhanden.
                </Typography>
              )}
            </>
          )}
        </Timeline>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Neuer Kommentar</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Titel"
              fullWidth
              value={newComment.title}
              disabled={saving}
              onChange={(e) =>
                setNewComment({ ...newComment, title: e.target.value })
              }
            />
            <TextField
              label="Beschreibung"
              fullWidth
              multiline
              rows={4}
              value={newComment.description}
              disabled={saving}
              onChange={(e) =>
                setNewComment({ ...newComment, description: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={saving}>
            Abbrechen
          </Button>
          <LoadingButton
            onClick={handleSave}
            loading={saving}
            variant="contained"
            disabled={!newComment.title}
          >
            Speichern
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default Comments;
